import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import useBreakpoint from '@src/hooks/useBreakpoint';
import { changeLocation } from '@src/core/store';
import { openCartModal } from '@src/features/cart/store/cart';
import { productSelector } from '@src/stores/product';
import {
  closeUpsellModal,
  productUpsellModalSelector,
  productUpsellOffersStatusSelector,
  productUpsellSelectedProductIdsSelector,
  toggleUpsellProductSelection,
  startMultiProductBooking,
} from '@src/stores/productUpsell';
import { trackUpsellContinued } from '../../analytics/upsellTracking';
import { useUpsellModalEventTrack } from './useUpsellModalEventTrack';

export const useProductUpsellModal = () => {
  const dispatch = useAppDispatch();
  const { isDesktop } = useBreakpoint();
  const modal = useAppSelector(productUpsellModalSelector);
  const upsellOffersStatusSelector = useMemo(
    () => productUpsellOffersStatusSelector(modal?.sourceProductId ?? ''),
    [modal?.sourceProductId],
  );
  const { offers } = useAppSelector(upsellOffersStatusSelector);
  const selectedProductIds = useAppSelector(productUpsellSelectedProductIdsSelector);
  const open = Boolean(modal && offers.length > 0);
  const sourceProduct = useAppSelector(productSelector(modal?.sourceProductId ?? ''));
  const openedAtRef = useUpsellModalEventTrack({
    open,
    modal,
    offersCount: offers.length,
    sellerAppId: sourceProduct?.appId ?? '',
  });

  const autoCheckoutRedirect = useWidgetParam('auto-checkout-redirect');
  const checkoutPage = useWidgetParam('checkout-page');
  const timeout = useWidgetParam('auto-checkout-redirect-timeout');
  const enableModal = useWidgetParam('enable-modal');
  const openCartWhenProductAddedParameter = useWidgetParam('open-cart-when-product-added');
  const showAsModal = enableModal && enableModal == 'true';
  const hasSelectedProducts = selectedProductIds.length > 0;

  const completePostAdd = useCallback(
    (timeOnModalMs: number, skipAutoCheckoutRedirect = false) => {
      dispatch(closeUpsellModal(timeOnModalMs));

      if (!skipAutoCheckoutRedirect && autoCheckoutRedirect && checkoutPage && /^[/#]/.test(checkoutPage)) {
        setTimeout(
          () => {
            dispatch(changeLocation(checkoutPage));
          },
          timeout ? +timeout : 3000,
        );
        return;
      }

      if (showAsModal || openCartWhenProductAddedParameter === 'true') {
        dispatch(openCartModal());
      }
    },
    [autoCheckoutRedirect, checkoutPage, dispatch, openCartWhenProductAddedParameter, showAsModal, timeout],
  );

  const closeAndCompletePostAdd = useCallback(() => {
    completePostAdd(Date.now() - openedAtRef.current);
  }, [completePostAdd]);

  const toggleProduct = useCallback(
    (productId: string) => {
      if (!modal) return;

      const offer = offers.find((o) => o.productId === productId);
      dispatch(toggleUpsellProductSelection(modal.sourceProductId, productId, offer?.product.appId ?? ''));
    },
    [dispatch, modal, offers],
  );

  const continueWithSelectedProducts = useCallback(() => {
    if (!hasSelectedProducts) return;

    trackUpsellContinued(modal!.sourceProductId, selectedProductIds.length, offers.length);

    dispatch(
      startMultiProductBooking(modal!.widgetId, modal!.sourceProductId, selectedProductIds, modal!.params, isDesktop),
    );
  }, [dispatch, modal, selectedProductIds, hasSelectedProducts, isDesktop, offers.length]);

  return {
    open,
    offers,
    selectedProductIds,
    closeAndCompletePostAdd,
    toggleProduct,
    continueWithSelectedProducts,
  };
};
